import React, { useEffect, useState } from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ProductCategoriesService from "services/product-categories.service";
import UploadService from "services/upload.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { FiSearch, FiPlus, FiEye } from "react-icons/fi";
import Select from "react-select";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  "product-categories": {
    data: null,
    firstLoad: true,
  },
};

const ProductCategories = () => {
  const [sorting, setSorting] = React.useState([]);
  // Initialize with cached data if available to prevent "No data found" flash
  const [data, setData] = useState(() => {
    const cache = tabCache["product-categories"];
    return cache?.data?.data || [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cache = tabCache["product-categories"];
    // Only show loading if no cache exists
    return cache?.firstLoad && !cache?.data;
  });
  const [searchText, setSearchText] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "active", "inactive"
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    icon: "",
    is_active: true
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Initialize pagination with cached data if available
  const [totalPages, setTotalPages] = useState(() => {
    const cache = tabCache["product-categories"];
    if (cache?.data?.totalCount) {
      return Math.ceil(cache.data.totalCount / 20);
    }
    return 1;
  });
  const [totalCount, setTotalCount] = useState(() => {
    const cache = tabCache["product-categories"];
    return cache?.data?.totalCount || 0;
  });

  const filterOptions = [
    { label: "All Categories", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      height: "40px",
      backgroundColor: "#0b1437",
      border: "none",
      boxShadow: "none",
      "&:hover": {
        border: "none",
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? "#24388a"
            : "#0b1437",
        color: isDisabled ? "#ccc" : "white",
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "#24388a"
              : "#1a2d6b"
            : undefined,
        },
        ":hover": {
          backgroundColor: isSelected ? "#24388a" : "#1a2d6b",
          color: "white",
        },
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      borderRadius: "0.75rem",
      overflow: "hidden",
    }),
    menuList: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      padding: 0,
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "white",
    }),
    input: (styles) => ({
      ...styles,
      color: "white",
    }),
  };

  const getProductCategories = async (showLoader = true) => {
    if (showLoader) {
    setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          filters: {
            ...(activeFilter !== "all" && { is_active: activeFilter === "active" ? 1 : 0 })
          },
          sort: {
            createdAt: -1
          }
        },
        searchtext: searchText,
      };
      const response = await ProductCategoriesService.getProductCategories(body);
      const responseData = response.data.data;
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache["product-categories"].data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache["product-categories"].firstLoad) {
        tabCache["product-categories"].firstLoad = false;
      }
      
      setData(responseData);
      setTotalCount(responseTotalCount);
      setTotalPages(Math.ceil(responseTotalCount / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch product categories");
    } finally {
      if (showLoader) {
      setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check cache first
    const cache = tabCache["product-categories"];
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(Math.ceil((cache.data.totalCount || 0) / pageSize));
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getProductCategories(shouldShowLoader);
  }, [currentPage, pageSize, searchText, activeFilter]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const handleFilterChange = (e) => {
    setActiveFilter(e.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.trim().length < 2) {
      newErrors.title = "Title must be at least 2 characters long";
    } else if (formData.title.trim().length > 50) {
      newErrors.title = "Title must be less than 50 characters";
    }
    
    // Image validation
    if (!formData.icon) {
      newErrors.icon = "Category icon is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      const response = await UploadService.uploadFile(file);
      if (response.data.files && response.data.files.length > 0) {
        const uploadedUrl = response.data.files[0];
        setUploadedImageUrl(uploadedUrl);
        setFormData(prev => ({
          ...prev,
          icon: uploadedUrl
        }));
        Toast("success", "Image uploaded successfully!");
      }
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to upload image");
      setSelectedFile(null);
      setFileInputKey(prev => prev + 1); // Reset file input
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddCategory = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      Toast("error", "Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const response = await ProductCategoriesService.updateProductCategory({
          _id: selectedCategoryId,
          ...formData
        });
        Toast("success", "Product category updated successfully");
      } else {
        const response = await ProductCategoriesService.createProductCategory(formData);
        Toast("success", "Product category created successfully");
      }
      setIsAddModalOpen(false);
      setFormData({ title: "", icon: "", is_active: true });
      setIsEditMode(false);
      setSelectedCategoryId(null);
      setSelectedFile(null);
      setUploadedImageUrl("");
      setFileInputKey(prev => prev + 1);
      setErrors({});
      document.body.style.overflow = "unset"; // Ensure body scroll is restored
      getProductCategories(false); // Refresh the list
    } catch (error) {
      Toast("error", error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} product category`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory = (category) => {
    setFormData({
      title: category.title || "",
      icon: category.icon || "",
      is_active: category.is_active || false
    });
    setUploadedImageUrl(category.icon || "");
    setSelectedCategoryId(category._id);
    setIsEditMode(true);
    setIsAddModalOpen(true);
    setErrors({});
    document.body.style.overflow = "hidden";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
    setIsEditMode(false);
    setSelectedCategoryId(null);
    setSelectedFile(null);
    setUploadedImageUrl("");
    setFileInputKey(prev => prev + 1);
    setErrors({});
    setFormData({ title: "", icon: "", is_active: true });
    document.body.style.overflow = "hidden";
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setFormData({ title: "", icon: "", is_active: true });
    setIsEditMode(false);
    setSelectedCategoryId(null);
    setSelectedFile(null);
    setUploadedImageUrl("");
    setFileInputKey(prev => prev + 1);
    setErrors({});
    document.body.style.overflow = "unset";
  };

  // Add useEffect to handle body scroll on component unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const columns = [
    columnHelper.accessor("icon", {
      id: "icon",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Icon</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <img
            src={info.getValue()}
            alt="Category Icon"
            className="h-10 w-10 rounded object-cover"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/40x40?text=No+Image";
            }}
          />
        </div>
      ),
    }),
    columnHelper.accessor("title", {
      id: "title",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Title</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Status</p>
      ),
      cell: (info) => {
        const status = info.getValue();
        
        return (
          <div className="flex items-center">
            <div
              className={`ms-center m-0 flex w-28 items-center justify-center rounded-lg ${
                status ? "bg-green-500/20" : ""
              } px-3 py-1.5 text-sm font-bold ${
                status ? "text-green-500" : ""
              }`}
              style={!status ? {
                backgroundColor: "rgba(255,37,37,0.1)",
                color: "#f06670d5"
              } : {}}
            >
              <span>{status ? "Active" : "In Active"}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Actions</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditCategory(info.row.original);
            }}
            className="flex items-center gap-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 text-sm font-medium text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 shadow-lg"
          >
            <FiEye className="h-4 w-4" />
            View
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <style>{`
        @media (max-width: 320px) {
          .mobile-second-row {
            flex-direction: column !important;
          }
          .mobile-dropdown-select {
            width: 100% !important;
            flex: none !important;
          }
          .mobile-add-button {
            width: 100% !important;
            flex: none !important;
          }
        }

           @media (min-width: 321px) and (max-width: 379px) {
          .mobile-second-row {
            flex-direction: row !important;
          }
          .mobile-dropdown-select {
            flex: 0.9 !important;
            min-width: 0;
          }
          .mobile-add-button {
            flex: 0.6 !important;
            min-width: 0;
          }
        }
        @media (min-width: 380px) and (max-width: 425px) {
          .mobile-second-row {
            flex-direction: row !important;
          }
          .mobile-dropdown-select {
            flex: 1 !important;
            min-width: 0;
          }
          .mobile-add-button {
            flex: 0.6 !important;
            min-width: 0;
          }
        }
        @media (min-width: 425px) and (max-width: 665px) {
          .mobile-second-row {
            justify-content: space-between !important;
          }
          .mobile-add-button {
            width: auto !important;
            min-width: 120px !important;
            flex: none !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
            margin-left: auto !important;
          }
          .mobile-dropdown-select {
            flex: none !important;
            width: auto !important;
            margin-right: auto !important;
          }
        }
        @media (min-width: 520px) and (max-width: 767px) {
          .main-container-row {
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
          }
          .main-container-row > div:first-child {
            width: auto !important;
            flex: 0 0 auto !important;
            margin-top: 0 !important;
          }
          .main-container-row > div:first-child > div {
            width: auto !important;
            min-width: 150px !important;
          }
          .main-container-row > div:first-child > div input {
            width: 120px !important;
            max-width: 120px !important;
          }
          .mobile-second-row {
            display: flex !important;
            flex-direction: row !important;
            margin-top: 0 !important;
            width: auto !important;
            flex: 0 0 auto !important;
            gap: 12px !important;
          }
          .mobile-dropdown-select {
            display: none !important;
          }
          .desktop-dropdown {
            display: block !important;
          }
          .mobile-add-button {
            flex: none !important;
            width: auto !important;
            min-width: 140px !important;
          }
        }
      `}</style>
      <div className="col-span-1 h-full w-full xl:col-span-1 min-w-0">
        <div className="flex flex-col md:flex-row md:justify-between gap-3 mb-2 main-container-row">
          <div className="mt-2 md:mt-4 flex h-fit justify-start w-full md:w-auto first-row-container">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white w-full md:w-auto xl:w-[170px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block flex-1 md:w-[90px] md:max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="mt-2 md:mt-4 flex h-fit items-center gap-3 w-full md:w-auto mobile-second-row">
            <Select
              className="h-full w-[158px] py-1 !text-white hidden md:block desktop-dropdown"
              classNamePrefix="select"
              defaultValue={filterOptions[0]}
              name="filter"
              options={filterOptions}
              value={filterOptions.find(option => option.value === activeFilter)}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionSelected={(option) => option.value === activeFilter}
              onChange={handleFilterChange}
              styles={colourStyles}
            />
            <Select
              className="h-full flex-1 md:hidden py-1 !text-white mobile-dropdown-select"
              classNamePrefix="select"
              defaultValue={filterOptions[0]}
              name="filter"
              options={filterOptions}
              value={filterOptions.find(option => option.value === activeFilter)}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionSelected={(option) => option.value === activeFilter}
              onChange={handleFilterChange}
              styles={colourStyles}
            />
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white transition duration-200 hover:opacity-90 flex-1 md:flex-none md:w-auto mobile-add-button"
            >
              <FiPlus className="h-4 w-4" />
              Add Category
            </button>
          </div>
        </div>
        <Card extra={"w-full h-fit pb-6 relative"} style={{ marginTop: '0.25rem' }}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0">
              <table className="table-fixed w-full border-collapse !mr-0 !pr-0 !border-r-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const columnId = header.column.id;
                      
                      // Set fixed widths for all columns to prevent layout shift
                      let columnWidth = 'auto';
                      if (columnId === 'icon') columnWidth = '150px';
                      else if (columnId === 'title') columnWidth = '180px';
                      else if (columnId === 'is_active') columnWidth = '170px';
                      else if (columnId === 'actions') columnWidth = '150px';
                      
                      // Reduce padding for title column to minimize gap before status
                      const paddingClass = columnId === 'title' 
                        ? `${isFirst ? 'pl-6' : 'pl-6'} pr-2`
                        : `${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`;
                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${paddingClass}`}
                          style={{ width: columnWidth }}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache["product-categories"]?.firstLoad && !tabCache["product-categories"]?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 align-middle">
                      <div className="flex items-center justify-center">
                        <p className="text-white">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                      return (
                        <tr 
                          key={row.id} 
                          onClick={() => handleEditCategory(row.original)}
                          className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}
                        >
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFirst = cellIndex === 0;
                            const isLast = cellIndex === row.getVisibleCells().length - 1;
                            const columnId = cell.column.id;
                            
                            // Set fixed widths matching header widths exactly
                            let columnWidth = 'auto';
                            if (columnId === 'icon') columnWidth = '120px';
                            else if (columnId === 'title') columnWidth = '200px';
                            else if (columnId === 'is_active') columnWidth = '150px';
                            else if (columnId === 'actions') columnWidth = '150px';
                            
                            // Reduce padding for title column to minimize gap before status
                            const paddingClass = columnId === 'title'
                              ? `${isFirst ? 'pl-6' : 'pl-6'} pr-2`
                              : `${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`;
                            
                            return (
                              <td
                                key={cell.id}
                                className={`border-white/0 py-3 ${paddingClass}`}
                                style={{ width: columnWidth }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>
      </div>

      {/* Add Category Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-96 max-h-[90vh] rounded-xl bg-navy-800 p-6 shadow-lg overflow-y-auto">
            <h2 className="mb-4 text-xl font-bold text-white">
              {isEditMode ? "Edit Product Category" : "Add Product Category"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full rounded-md bg-navy-700 px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 border-2 transition-colors duration-200 ${
                    errors.title 
                      ? 'border-red-500 hover:border-red-400' 
                      : 'border-gray-600 hover:border-blue-400'
                  }`}
                  placeholder="Enter category title"
                />
                {errors.title && (
                  <p className="mt-1 text-xs text-red-400">{errors.title}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category Icon
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="icon-upload"
                      disabled={isUploading}
                      key={fileInputKey}
                    />
                    <label
                      htmlFor="icon-upload"
                      className={`flex items-center gap-2 rounded-full backdrop-blur-md border px-4 py-2 text-sm font-medium text-white transition-all duration-300 shadow-lg cursor-pointer disabled:opacity-50 ${
                        errors.icon 
                          ? 'bg-red-500/10 border-red-400 hover:bg-red-500/20' 
                          : 'bg-white/10 border-white/20 hover:bg-white/20 hover:border-white/30'
                      }`}
                    >
                      {isUploading ? (
                        <img src={Loader} alt="Uploading..." className="h-4 w-4" />
                      ) : (
                        <FiPlus className="h-4 w-4" />
                      )}
                      {isUploading ? "Uploading..." : "Choose Image"}
                    </label>
                  </div>
                  {errors.icon && (
                    <p className="text-xs text-red-400">{errors.icon}</p>
                  )}
                  {uploadedImageUrl && (
                    <div className="flex items-center gap-3">
                      <img
                        src={uploadedImageUrl}
                        alt="Category Icon"
                        className="h-16 w-16 rounded-lg object-cover border-2 border-white/20"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/64x64?text=No+Image";
                        }}
                      />
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 truncate">
                          {selectedFile ? selectedFile.name : "Uploaded Image"}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedImageUrl("");
                            setSelectedFile(null);
                            setFormData(prev => ({ ...prev, icon: "" }));
                            // Clear icon error when image is removed
                            if (errors.icon) {
                              setErrors(prev => ({ ...prev, icon: undefined }));
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-300 mt-1"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      formData.is_active ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        formData.is_active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-400">
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={closeAddModal}
                className="rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                disabled={isSubmitting}
                className="rounded-md bg-primary-gradient px-4 py-2 text-white hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <img src={Loader} alt="Loading..." className="mx-auto h-6 w-6" />
                ) : (
                  isEditMode ? "Update Category" : "Add Category"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategories; 